import type { ComponentProps } from 'react'
import { computeRiskScore, deriveRating, RISK_LEVEL_LABELS } from '@deepguide-ai/types'
import type { AuditorVendor } from '@deepguide-ai/types'
import { Button } from '@deepguide-ai/ui/button'
import { Checkbox } from '@deepguide-ai/ui/checkbox'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@deepguide-ai/ui/select'
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@deepguide-ai/ui/table'
import { AlertTriangle, CheckCircle2, Eye } from 'lucide-react'
import { vendorLogo } from '@/lib/vendor-logo'
import { TablePaginationFooter } from '@/components/ui/TablePaginationFooter'
import { RoleSelect } from '@/components/compliance/RoleSelect'
import { AssessmentStatusBadge } from '@/components/compliance/VendorAssessmentDialog'
import {
  CategoryLabel,
  RiskTierBadge,
  VendorStatusBadge,
} from './BadgeComponents'

function BaaStatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    NOT_REQUIRED: 'bg-muted text-text-subtle',
    REQUIRED: 'bg-status-red-bg text-status-red',
    SIGNED: 'bg-status-green-bg text-status-green',
    EXPIRED: 'bg-status-orange-bg text-status-orange',
  }
  const labels: Record<string, string> = {
    NOT_REQUIRED: '—',
    REQUIRED: 'Needed',
    SIGNED: 'Signed',
    EXPIRED: 'Expired',
  }
  const cls = styles[status] ?? styles.NOT_REQUIRED
  return (
    <span
      className={`font-body inline-flex h-[20px] items-center rounded-md px-2 text-[10px] font-semibold tracking-[0.5px] uppercase ${cls}`}
    >
      {labels[status] ?? status}
    </span>
  )
}

function formatDate(date: Date | string | null | undefined): string {
  if (!date) return '—'
  const d = typeof date === 'string' ? new Date(date) : date
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

export function VendorList({
  items,
  hipaaEnabled,
  page,
  pageSize,
  totalItems,
  onPageChange,
  onPageSizeChange,
  onOpenVendor,
  onOpenRisks,
  management,
}: {
  items: AuditorVendor[]
  hipaaEnabled: boolean
  page: number
  pageSize: number
  totalItems: number
  onPageChange: (page: number) => void
  onPageSizeChange: (pageSize: number) => void
  onOpenVendor: (id: string) => void
  onOpenRisks?: (id: string) => void
  management?: {
    selectedIds: Set<string>
    updating: boolean
    starting: boolean
    roles: ComponentProps<typeof RoleSelect>['roles']
    clearSelection: () => void
    toggleSelectAll: () => void
    toggleSelect: (id: string) => void
    handleBulkTierUpdate: (tier: string) => void
    handleBulkResidualTierUpdate: (tier: string | null) => void
    handleBulkUpdate: (data: {
      ownerId?: string | null
      ownerLabel?: string
      status?: string
    }) => void
    handleBulkStartAssessment: () => void
  }
}) {
  const hasSelection = Boolean(management && management.selectedIds.size > 0)
  return (
    <div className='flex min-h-0 items-start gap-0'>
      <div className='border-border bg-card w-full overflow-hidden rounded-md border transition-all'>
        <Table className='w-full'>
          <TableHeader className='sticky top-0 z-10'>
            <TableRow
              className={`border-border h-[40px] border-b ${hasSelection ? 'bg-selected' : 'bg-surface-subtle'}`}
            >
              {management && (
                <TableHead className='w-[36px] px-3'>
                  <Checkbox
                    checked={
                      items.length > 0 &&
                      items.every((i) => management?.selectedIds.has(i.id))
                    }
                    onCheckedChange={management?.toggleSelectAll}
                    className='h-3.5 w-3.5 rounded-sm'
                  />
                </TableHead>
              )}

              {/* ID column — swaps to selection count */}
              <TableHead className='font-body text-text-subtle w-[72px] px-3 text-left text-[10px] font-semibold tracking-[1px] uppercase'>
                {hasSelection ? (
                  <span className='flex items-center gap-2 tracking-normal whitespace-nowrap normal-case'>
                    <span className='font-body text-cobalt-strong text-[11px] font-semibold'>
                      {management?.selectedIds.size} selected
                      {management?.updating && '...'}
                    </span>
                    <button
                      onClick={() => management?.clearSelection()}
                      className='font-body text-text-subtle hover:text-text-body text-[10px]'
                    >
                      Clear
                    </button>
                  </span>
                ) : (
                  'ID'
                )}
              </TableHead>

              {/* Name column — capped: just the logo + name */}
              <TableHead className='font-body text-text-subtle w-[180px] px-3 text-left text-[10px] font-semibold tracking-[1px] uppercase'>
                {hasSelection ? '' : 'Name'}
              </TableHead>

              <>
                {/* Category */}
                <TableHead className='font-body text-text-subtle w-[110px] px-3 text-left text-[10px] font-semibold tracking-[1px] uppercase'>
                  {hasSelection ? '' : 'Category'}
                </TableHead>

                {/* Inherent Risk — bulk selectable */}
                <TableHead className='w-[85px] px-2 text-left align-middle'>
                  {hasSelection ? (
                    <Select
                      onValueChange={(v) => management?.handleBulkTierUpdate(v)}
                    >
                      <SelectTrigger className='border-input bg-card h-6 w-full rounded-sm text-[10px]'>
                        <SelectValue placeholder='Inherent' />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value='CRITICAL'>Critical</SelectItem>
                        <SelectItem value='HIGH'>High</SelectItem>
                        <SelectItem value='MEDIUM'>Medium</SelectItem>
                        <SelectItem value='LOW'>Low</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <span className='font-body text-text-subtle text-[10px] font-semibold tracking-[1px] uppercase'>
                      Inherent
                    </span>
                  )}
                </TableHead>

                {/* Residual Risk — bulk selectable */}
                <TableHead className='w-[85px] px-2 text-left align-middle'>
                  {hasSelection ? (
                    <Select
                      onValueChange={(v) =>
                        management?.handleBulkResidualTierUpdate(
                          v === '_unset' ? null : v
                        )
                      }
                    >
                      <SelectTrigger className='border-input bg-card h-6 w-full rounded-sm text-[10px]'>
                        <SelectValue placeholder='Assessed' />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value='CRITICAL'>Critical</SelectItem>
                        <SelectItem value='HIGH'>High</SelectItem>
                        <SelectItem value='MEDIUM'>Medium</SelectItem>
                        <SelectItem value='LOW'>Low</SelectItem>
                        <SelectItem value='_unset'>Unassessed</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <span className='font-body text-text-subtle text-[10px] font-semibold tracking-[1px] uppercase'>
                      Assessed
                    </span>
                  )}
                </TableHead>

                {/* Linked register risks (VendorRiskLink count) */}
                <TableHead className='font-body text-text-subtle w-[56px] px-3 text-left text-[10px] font-semibold tracking-[1px] uppercase'>
                  {hasSelection ? '' : 'Risks'}
                </TableHead>

                {/* Owner — bulk selectable */}
                <TableHead className='w-[110px] px-2 text-left align-middle'>
                  {hasSelection ? (
                    <RoleSelect
                      roles={management?.roles ?? []}
                      onValueChange={(v) => {
                        const role = (management?.roles ?? []).find(
                          (r) => r.id === v
                        )
                        management?.handleBulkUpdate({
                          ownerId: v,
                          ownerLabel: role?.name ?? 'Unassigned',
                        })
                      }}
                      ariaLabel='Bulk vendor owner role'
                      noneLabel='Unassigned'
                      placeholder='Owner'
                      className='border-input bg-card h-6 w-full rounded-sm text-[10px]'
                    />
                  ) : (
                    <span className='font-body text-text-subtle text-[10px] font-semibold tracking-[1px] uppercase'>
                      Owner
                    </span>
                  )}
                </TableHead>

                {/* Customer Data */}
                <TableHead className='font-body text-text-subtle w-[110px] px-3 text-left text-[10px] font-semibold tracking-[1px] uppercase'>
                  {hasSelection ? '' : 'Customer Data'}
                </TableHead>

                {/* BAA (HIPAA only) */}
                {hipaaEnabled && (
                  <TableHead className='font-body text-text-subtle w-[100px] px-3 text-left text-[10px] font-semibold tracking-[1px] uppercase'>
                    {hasSelection ? '' : 'BAA'}
                  </TableHead>
                )}
              </>

              {/* Status — bulk selectable */}
              <TableHead className='w-[110px] px-2 text-left align-middle'>
                {hasSelection ? (
                  <Select
                    onValueChange={(v) =>
                      management?.handleBulkUpdate({ status: v })
                    }
                  >
                    <SelectTrigger className='border-input bg-card h-6 w-full rounded-sm text-[10px]'>
                      <SelectValue placeholder='Status' />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='ACTIVE'>Active</SelectItem>
                      <SelectItem value='UNDER_REVIEW'>Under Review</SelectItem>
                      <SelectItem value='PENDING_ONBOARDING'>
                        Pending
                      </SelectItem>
                      <SelectItem value='OFFBOARDED'>Offboarded</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <span className='font-body text-text-subtle text-[10px] font-semibold tracking-[1px] uppercase'>
                    Status
                  </span>
                )}
              </TableHead>

              {/* Assessment — bulk "Start Assessment" */}
              <TableHead className='w-[110px] px-2 text-left align-middle'>
                {hasSelection ? (
                  <Button
                    size='sm'
                    className='bg-primary text-primary-foreground hover:bg-primary/90 h-6 rounded-sm px-2 text-[10px]'
                    onClick={management?.handleBulkStartAssessment}
                    disabled={management?.starting}
                  >
                    {management?.starting ? 'Starting...' : 'Start Assessment'}
                  </Button>
                ) : (
                  <span className='font-body text-text-subtle text-[10px] font-semibold tracking-[1px] uppercase'>
                    Security Review
                  </span>
                )}
              </TableHead>

              {/* Actions column */}
              <TableHead className='w-[72px] px-3 text-center'>
                <span className='font-body text-text-subtle text-[10px] font-semibold tracking-[1px] uppercase'>
                  Action
                </span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((item) => {
              const isOverdue =
                item.nextReviewDate &&
                new Date(item.nextReviewDate) < new Date()
              return (
                <TableRow
                  key={item.id}
                  className={`border-border cursor-pointer border-b transition-colors last:border-b-0 ${
                    management?.selectedIds.has(item.id)
                      ? 'bg-selected'
                      : 'hover:bg-surface-subtle'
                  }`}
                  onClick={() => onOpenVendor(item.id)}
                >
                  {management && (
                    <TableCell
                      className='px-3 py-2.5'
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Checkbox
                        checked={management?.selectedIds.has(item.id)}
                        onCheckedChange={() =>
                          management?.toggleSelect(item.id)
                        }
                        className='h-3.5 w-3.5 rounded-sm'
                      />
                    </TableCell>
                  )}
                  {/* ID */}
                  <TableCell className='font-body text-cobalt-strong px-3 py-2.5 text-[12px] font-medium whitespace-nowrap'>
                    {item.displayId}
                  </TableCell>
                  {/* Name */}
                  <TableCell className='max-w-[180px] px-3 py-2.5'>
                    <div className='font-body text-foreground flex items-center gap-2 truncate text-[12px] font-medium'>
                      <img
                        src={item.logoUrl ?? vendorLogo(item.name) ?? ''}
                        alt=''
                        width={18}
                        height={18}
                        className='h-[18px] w-[18px] shrink-0 rounded-none object-contain'
                        onError={(e) => {
                          ;(e.currentTarget as HTMLImageElement).style.display =
                            'none'
                        }}
                      />
                      {item.name}
                    </div>
                  </TableCell>
                  <>
                    {/* Category */}
                    <TableCell className='px-3 py-2.5'>
                      <CategoryLabel category={item.category} />
                    </TableCell>

                    {/* Inherent Risk — derived tier + its axes */}
                    <TableCell className='px-3 py-2.5'>
                      {item.inherentImpact && item.inherentLikelihood ? <RiskTierBadge tier={deriveRating(item.inherentLikelihood, item.inherentImpact)} score={computeRiskScore(item.inherentLikelihood, item.inherentImpact)} /> : <span className='font-body text-text-disabled text-[10px]'>Unassessed</span>}
                      {item.inherentImpact && (
                        <div
                          className='font-body text-text-subtle mt-0.5 text-[9.5px] whitespace-nowrap'
                          title={`Impact ${RISK_LEVEL_LABELS[item.inherentImpact]} × likelihood ${item.inherentLikelihood ? RISK_LEVEL_LABELS[item.inherentLikelihood] : 'pending assessment'}`}
                        >
                          Imp {item.inherentImpact.split('_').map(word => word[0]).join('')} × Lik{' '}
                          {item.inherentLikelihood?.split('_').map(word => word[0]).join('') ?? '?'}
                        </div>
                      )}
                    </TableCell>

                    {/* Residual Risk — derived tier + its axes */}
                    <TableCell className='px-3 py-2.5'>
                      {item.residualRiskTier && item.residualImpact && item.residualLikelihood ? (
                        <>
                          <RiskTierBadge tier={deriveRating(item.residualLikelihood, item.residualImpact)} score={computeRiskScore(item.residualLikelihood, item.residualImpact)} />
                          {item.residualImpact && (
                            <div
                              className='font-body text-text-subtle mt-0.5 text-[9.5px] whitespace-nowrap'
                              title={`Impact ${RISK_LEVEL_LABELS[item.residualImpact]} × likelihood ${item.residualLikelihood ? RISK_LEVEL_LABELS[item.residualLikelihood] : 'unassessed'}`}
                            >
                              Imp {item.residualImpact.split('_').map(word => word[0]).join('')} × Lik{' '}
                              {item.residualLikelihood?.split('_').map(word => word[0]).join('') ?? '?'}
                            </div>
                          )}
                        </>
                      ) : (
                        <span className='font-body text-text-disabled text-[10px] italic'>
                          Unassessed
                        </span>
                      )}
                    </TableCell>

                    {/* Linked risks */}
                    <TableCell className='px-3 py-2.5'>
                      {item.riskCount > 0 ? (
                        onOpenRisks ? (
                          <button
                            type='button'
                            onClick={(event) => {
                              event.stopPropagation()
                              onOpenRisks(item.id)
                            }}
                            className='font-body text-cobalt-strong text-[11px] font-medium tabular-nums hover:underline'
                          >
                            {item.riskCount}
                          </button>
                        ) : (
                          <span className='font-body text-text-body text-[11px] tabular-nums'>
                            {item.riskCount}
                          </span>
                        )
                      ) : (
                        <span className='font-body text-text-placeholder text-[10px] tabular-nums'>
                          0
                        </span>
                      )}
                    </TableCell>

                    {/* Owner */}
                    <TableCell className='font-body text-text-body px-3 py-2.5 text-[11px]'>
                      {item.ownerRoleName ?? item.ownerLabel}
                    </TableCell>

                    {/* Customer Data */}
                    <TableCell className='px-3 py-2.5 text-center'>
                      {item.customerDataInvolved ? (
                        <span className='font-body text-status-red text-[10px] font-semibold tracking-[0.5px] uppercase'>
                          Yes
                        </span>
                      ) : (
                        <span className='font-body text-text-placeholder text-[10px]'>
                          No
                        </span>
                      )}
                    </TableCell>

                    {/* BAA (HIPAA only) */}
                    {hipaaEnabled && (
                      <TableCell className='px-3 py-2.5'>
                        {item.containsEphi ? (
                          <BaaStatusBadge
                            status={item.baaStatus ?? 'REQUIRED'}
                          />
                        ) : (
                          <span className='font-body text-text-disabled text-[10px]'>
                            —
                          </span>
                        )}
                      </TableCell>
                    )}
                  </>
                  {/* Status */}
                  <TableCell className='px-3 py-2.5'>
                    <VendorStatusBadge status={item.status} />
                  </TableCell>
                  {/* Security review — one glanceable cell: state on
                              the first line, cadence on the second (the old
                              Assessment / Approved At / Next Review columns). */}
                  <TableCell className='px-3 py-2.5'>
                    {item.latestAssessment?.status === 'APPROVED' ? (
                      <span
                        className={`font-body inline-flex items-center gap-1 text-[11px] font-medium ${isOverdue ? 'text-status-red' : 'text-status-green'}`}
                      >
                        {isOverdue ? (
                          <AlertTriangle className='h-3 w-3' />
                        ) : (
                          <CheckCircle2 className='h-3 w-3' />
                        )}
                        {isOverdue ? 'Review overdue' : 'Up-to-date'}
                      </span>
                    ) : item.latestAssessment ? (
                      <AssessmentStatusBadge
                        status={item.latestAssessment.status}
                      />
                    ) : (
                      <span className='font-body text-text-disabled text-[10px] italic'>
                        Not reviewed
                      </span>
                    )}
                    {(item.nextReviewDate ||
                      item.latestAssessment?.status === 'APPROVED') && (
                      <div
                        className={`font-body mt-0.5 text-[10px] whitespace-nowrap ${isOverdue ? 'text-status-red' : 'text-text-subtle'}`}
                      >
                        {item.latestAssessment?.status === 'APPROVED' &&
                          `reviewed ${formatDate(item.latestAssessment.reviewedAt)}`}
                        {item.latestAssessment?.status === 'APPROVED' &&
                          item.nextReviewDate &&
                          ' · '}
                        {item.nextReviewDate &&
                          `${isOverdue ? 'was due' : 'next due'} ${formatDate(item.nextReviewDate)}`}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className='px-3 py-2.5 text-center'>
                    <button
                      type='button'
                      aria-label={`View ${item.displayId} detail`}
                      className='text-text-subtle hover:bg-muted hover:text-foreground inline-flex h-7 w-7 items-center justify-center rounded-sm transition-colors'
                      onClick={(event) => {
                        event.stopPropagation()
                        onOpenVendor(item.id)
                      }}
                    >
                      <Eye className='h-3.5 w-3.5' />
                    </button>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
        <TablePaginationFooter
          page={page}
          pageSize={pageSize}
          totalItems={totalItems}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
        />
      </div>
    </div>
  )
}
